import { useCallback } from "react";

import { useDealParamsHooks, useModals } from "hooks";
import { useDealActions, useDocumentsActions } from "store";
import { IFile } from "store/types";
import { IFileMetaDetail, IMetaDetails } from "store/types/deal/folder";

import { EntityType } from "../../../constants";

const isFileItem = (item: { type: string }): item is IFileMetaDetail =>
  item.type !== EntityType.DOCUMENT && item.type !== EntityType.FORM && item.type !== EntityType.FOLDER;

interface UseBulkActionsHookParams {
  selectedItems: IMetaDetails[];
  setSelectedItems: React.Dispatch<React.SetStateAction<IMetaDetails[]>>;
  useItemDealId?: boolean;
}

export const useBulkActionsHook = ({
  selectedItems,
  setSelectedItems,
  useItemDealId
}: UseBulkActionsHookParams) => {
  const { dealId: urlDealId } = useDealParamsHooks();
  const { deleteModal } = useModals();
  const { downloadFileAction, deleteFormAction, deleteFileAction, deleteFolderAction, patchFolderMetaDetailAction } =
    useDealActions();
  const { deleteDocumentAction } = useDocumentsActions();

  const getItemDealId = useCallback(
    (item: IMetaDetails) => (useItemDealId ? ((item as IFileMetaDetail).deal?.id ?? null) : urlDealId) as number,
    [useItemDealId, urlDealId]
  );

  const handleBulkDelete = useCallback(() => {
    if (!selectedItems.length) return;
    if (!useItemDealId && !urlDealId) return;

    const count = selectedItems.length;
    deleteModal({
      message: `Are you sure you want to delete ${count} selected ${count === 1 ? "item" : "items"}?`,
      action: async () => {
        const itemsToDelete = [...selectedItems];
        setSelectedItems([]);
        for (const item of itemsToDelete) {
          const itemDealId = getItemDealId(item);
          if (item.type === EntityType.FOLDER) {
            await deleteFolderAction({ dealId: itemDealId, folderId: item.id });
          } else if (item.type === EntityType.DOCUMENT) {
            await deleteDocumentAction(item.id);
          } else if (item.type === EntityType.FORM) {
            await deleteFormAction({ dealId: itemDealId, formId: item.id });
          } else if (isFileItem(item)) {
            await deleteFileAction({ id: item.id, deal_id: itemDealId } as IFile);
          }
          patchFolderMetaDetailAction({ key: "delete", id: item.id, entityType: item.entity_type });
        }
      }
    });
  }, [
    selectedItems,
    urlDealId,
    useItemDealId,
    deleteModal,
    deleteDocumentAction,
    deleteFolderAction,
    deleteFormAction,
    deleteFileAction,
    setSelectedItems,
    patchFolderMetaDetailAction,
    getItemDealId
  ]);

  const handleBulkDownload = useCallback(async () => {
    if (!useItemDealId && !urlDealId) return;
    const fileItems = selectedItems.filter(isFileItem);
    for (const item of fileItems) {
      const itemDealId = getItemDealId(item);
      await downloadFileAction({ fileId: item.id, fileName: item.name, dealId: itemDealId });
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  }, [selectedItems, urlDealId, useItemDealId, downloadFileAction, getItemDealId]);

  return { handleBulkDelete, handleBulkDownload };
};
